/// <amd-module name="Wit/Common/SprintSettings/Components/SprintSettingsPanel" />
import * as React from "react";
import { TeamContext } from "Tfs/Platform/Contracts/Core";
import { useTfsProject } from "Tfs/Platform/TfsComponent";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { newGuid } from "VSS/Core/Util/String";
import { Components, usePageContext } from "VSS/Platform/Layout";
import { IListBoxGroup } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import * as Resources from "Wit/Common/SprintSettings/Resources";
import { saveCardStyleSettings, saveTaskboardFieldSettings } from "Wit/Common/SprintSettings/Utils/SaveSettingsUtils";
import { ISprintSettingsPanelProps } from "Wit/Common/SprintSettingsTypes/Components/SprintSettingsPanel.Props";
import { TeamSettingsPanel } from "Wit/Common/TeamSettingsPanel/Components/TeamSettingsPanel";
import { ITeamPermissions } from "Wit/Common/TeamSettingsPanel/Contracts/Contracts";
import { ITeamSettingsPanelService } from "Wit/Common/TeamSettingsPanel/Services/TeamSettingsPanelService";
import { ISettingsView } from "Wit/Common/WitSettingsPanelTypes/Components/WitSettingsPanel.props";
import { BoardFieldSettings, CardSettingsDict } from "Wit/Common/WitSettingsViews/Components/CardSettingsViews/BoardFieldSettings";
import { getBoardFieldSettingsTab } from "Wit/Common/WitSettingsViews/Components/CardSettingsViews/BoardFieldSettingsView";
import { ICriteriaSettingData } from "Wit/Common/WitSettingsViews/Components/CriteriaTable/CriteriaInterfaces";
import { getCriteriaSelectedSettingData } from "Wit/Common/WitSettingsViews/Components/CriteriaTable/CriteriaService";
import { IStylesRuleData } from "Wit/Common/WitSettingsViews/Components/StylesTable/StyleInterfaces";
import { IStyleService } from "Wit/Common/WitSettingsViews/Components/StylesTable/StyleService";
import { getStyleSettingsTab } from "Wit/Common/WitSettingsViews/Components/StylesTable/StyleTable";
import { SprintsTabs } from "Wit/SprintsHub/Common/Constants";
import { ISprintTaskboardData, IStyleRule } from "Wit/SprintsHub/Common/Contracts/Interfaces";

export const SprintSettingsPanel: React.FunctionComponent<ISprintSettingsPanelProps> = ({
    onDismiss,
    onSave,
    selectedTabId,
    taskboardData,
    teamId
}) => {
    const pageContext = usePageContext();
    const projectId = useTfsProject()?.id ?? "";

    const teamSettingsPanelService = pageContext.getService<ITeamSettingsPanelService>("ITeamSettingsPanelService");
    return (
        <Observer taskboardData={taskboardData} selectedTabId={selectedTabId} permissions={teamSettingsPanelService.getTeamPermissions(teamId)}>
            {(props: { permissions: ITeamPermissions | undefined }) => {
                const { permissions } = props;
                if (!taskboardData.value && selectedTabId.value === SprintsTabs.Taskboard || !permissions) {
                    return null;
                }
                const disabled = !permissions?.CurrentUserHasTeamAdminPermission;

                let settingsViews: ISettingsView[] = [];
                let wrappedOnDismiss = onDismiss;
                if (taskboardData.value && selectedTabId.value === SprintsTabs.Taskboard && !taskboardData.value.taskboardModel.isEmpty) {
                    const teamContext = {
                        teamId: taskboardData.value.boardCardSettings.scopeId,
                        projectId
                    } as TeamContext;

                    const rules = getStyleSettingData(taskboardData.value.boardCardSettings.styles);

                    const childTypes = new Set(taskboardData.value.taskboardModel.childWorkItemTypes);
                    const remainingWorkFieldReference = getRemainingFieldReference(taskboardData.value);
                    const boardFieldSettings = new BoardFieldSettings(
                        taskboardData.value.boardCardSettings.cards as unknown as CardSettingsDict,
                        childTypes
                    );

                    settingsViews = [
                        getBoardFieldSettingsTab(boardFieldSettings, remainingWorkFieldReference, disabled, TaskboardSettingsGroup.Cards, () =>
                            saveTaskboardFieldSettings(pageContext, teamContext, boardFieldSettings)
                        ),
                        getStyleSettingsTab(pageContext, rules, disabled, TaskboardSettingsGroup.Cards, () =>
                            saveCardStyleSettings(pageContext, teamContext)
                        )
                    ];

                    wrappedOnDismiss = () => {
                        const styleService = pageContext.getService<IStyleService>("IStyleService");
                        styleService.clear();
                        onDismiss();
                    };
                }

                return (
                    <TeamSettingsPanel
                        onDismiss={wrappedOnDismiss}
                        onSave={onSave}
                        settingsGroups={settingsGroups}
                        settingsViews={settingsViews}
                        teamId={teamId}
                        titleProps={{ text: Resources.SettingsPanelTitle }}
                    />
                );
            }}
        </Observer>
    );
};

const TaskboardSettingsGroup = {
    Cards: "cards",
    Boards: "boards"
} as const;

const settingsGroups: IListBoxGroup[] = [
    {
        id: TaskboardSettingsGroup.Cards,
        name: "Cards"
    }
];

Components.add("ms.vss-work-web.sprint-settings-panel-component", SprintSettingsPanel);

function getRemainingFieldReference(taskboardData: ISprintTaskboardData) {
    const taskboardModel = taskboardData.taskboardModel;
    const remainingWorkFieldName = taskboardModel.workRollupField;
    const field = taskboardModel.fieldDefinitions.find(field => field.ReferenceName === remainingWorkFieldName);
    if (field) {
        return { id: field.Id, referenceName: field.ReferenceName };
    }
}

/**
 * Custom converter since sprints hub isn't using generated contracts
 */
function getStyleSettingData(styles: IStyleRule[]): IStylesRuleData[] {
    return styles.map(style => {
        return {
            ruleName: new ObservableValue<string>(style.name),
            isEnabled: new ObservableValue<boolean>(style.isEnabled),
            color: new ObservableValue<string>(style.styles?.["background-color"] ?? ""),
            titleColor: new ObservableValue<string>((style.styles as any)["title-color"]),
            isTitleItalicized: new ObservableValue<boolean>(!!(style.styles as any)["title-font-style"]),
            isTitleBold: new ObservableValue<boolean>(!!(style.styles as any)["title-font-weight"]),
            isTitleUnderlined: new ObservableValue<boolean>(!!(style.styles as any)["title-text-decoration"]),
            id: newGuid(),
            selectedCriteriaData: getCriteriaSelectedSettingData(style.criteria?.clauses ?? []),
            criteriaData: new ObservableArray<ICriteriaSettingData>([])
        };
    });
}
